<template>
  <div class="accesos-registros">
    <div class="chart-card">
      <!-- Cabecera: total + selector de límite -->
      <div class="registros-toolbar">
        <div class="registros-total">
          <i class="fas fa-list-ul"></i>
          <span v-if="!loading">
            <strong>{{ fmt(totalRecords) }}</strong> registro{{ totalRecords === 1 ? '' : 's' }}
          </span>
          <span v-else class="spinner-border spinner-border-sm text-success" role="status"></span>
        </div>
        <div class="registros-limit">
          <label class="me-2 mb-0">Mostrar</label>
          <select class="form-select form-select-sm" v-model.number="limit" @change="onLimitChange">
            <option v-for="opt in limitOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>
      </div>

      <!-- Tabla -->
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0 registros-table">
          <thead>
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                :class="[col.thClass, { sortable: col.sortField }]"
                @click="col.sortField && toggleSort(col.sortField)"
              >
                {{ col.label }}
                <i v-if="col.sortField" :class="sortIcon(col.sortField)" class="sort-icon"></i>
              </th>
              <th class="text-end">Detalle</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="columns.length + 1" class="text-center py-5">
                <div class="spinner-border text-success" role="status"></div>
              </td>
            </tr>
            <template v-else>
              <tr v-for="r in records" :key="r._id">
                <td>
                  <div class="fw-semibold">{{ r.fechaIngreso }}</div>
                  <div class="text-muted small">{{ r.horaIngreso }}</div>
                </td>
                <td>
                  <div class="fw-semibold">{{ r.nombrePersona }}</div>
                  <div class="d-md-none text-muted small">{{ r.rutSocio }}</div>
                </td>
                <td class="d-none d-md-table-cell">{{ r.rutSocio }}</td>
                <td class="d-none d-lg-table-cell">
                  <span class="badge bg-light text-dark">{{ r.perfilPersona }}</span>
                </td>
                <td>
                  <span class="badge" :class="estadoClass(r.estadoAcceso)">{{ r.estadoAcceso }}</span>
                </td>
                <td class="d-none d-sm-table-cell">
                  <div>{{ r.metodoAcceso }}</div>
                  <div v-if="r.detalleMetodoAcceso" class="text-muted small">{{ r.detalleMetodoAcceso }}</div>
                </td>
                <td class="d-none d-xl-table-cell">{{ r.idAcceso }}</td>
                <td class="d-none d-lg-table-cell">{{ r.nombreSucursalAcceso }}</td>
                <td class="d-none d-xl-table-cell">{{ r.planAcceso }}</td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-secondary" @click="openDetail(r)" title="Ver detalle">
                    <i class="fas fa-eye"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="!records.length">
                <td :colspan="columns.length + 1" class="text-center text-muted py-5">
                  <i class="fas fa-inbox fa-2x d-block mb-2" style="opacity: 0.3;"></i>
                  No hay registros de acceso para los filtros seleccionados.
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="!loading && totalPages > 1" class="registros-pagination">
        <button class="btn btn-sm btn-outline-secondary" :disabled="page === 0" @click="goTo(0)" title="Primera">
          <i class="fas fa-angle-double-left"></i>
        </button>
        <button class="btn btn-sm btn-outline-secondary" :disabled="page === 0" @click="goTo(page - 1)" title="Anterior">
          <i class="fas fa-angle-left"></i>
        </button>
        <button
          v-for="p in pageWindow"
          :key="p"
          class="btn btn-sm"
          :class="p === page ? 'btn-success' : 'btn-outline-secondary'"
          @click="goTo(p)"
        >{{ p + 1 }}</button>
        <button class="btn btn-sm btn-outline-secondary" :disabled="page >= totalPages - 1" @click="goTo(page + 1)" title="Siguiente">
          <i class="fas fa-angle-right"></i>
        </button>
        <button class="btn btn-sm btn-outline-secondary" :disabled="page >= totalPages - 1" @click="goTo(totalPages - 1)" title="Última">
          <i class="fas fa-angle-double-right"></i>
        </button>
        <span class="registros-pagination-info">Página {{ page + 1 }} de {{ totalPages }}</span>
      </div>

      <p v-if="timezone && !loading" class="text-muted small text-end mt-3 mb-0">
        <i class="fas fa-clock"></i> Horarios en zona horaria: <strong>{{ timezone }}</strong>
      </p>
    </div>

    <!-- Modal de detalle -->
    <div v-if="detail" class="accesos-modal-overlay" @click.self="detail = null">
      <div class="accesos-modal">
        <div class="accesos-modal-header">
          <h6 class="mb-0"><i class="fas fa-id-badge me-2"></i>Detalle del acceso</h6>
          <button class="btn-close-custom" @click="detail = null"><i class="fas fa-times"></i></button>
        </div>
        <div class="accesos-modal-body">
          <div class="detail-row" v-for="item in detailFields" :key="item.label">
            <span class="detail-label">{{ item.label }}</span>
            <span class="detail-value">
              <span v-if="item.badge" class="badge" :class="estadoClass(detail.estadoAcceso)">{{ item.value }}</span>
              <template v-else>{{ item.value }}</template>
            </span>
          </div>
        </div>
        <div class="accesos-modal-footer">
          <button class="btn btn-success" @click="detail = null">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const OP_REGISTROS = '/v1/access/get/gymaccess';

export default {
  name: 'AccesosRegistros',
  props: {
    // Snapshot de filtros comprometido por el padre (sin page/limit/sort).
    parameters: {
      type: Object,
      default: () => ({})
    },
    // Cambia cada vez que el padre aplica/limpia filtros: dispara recarga.
    paramsVersion: {
      type: Number,
      default: 0
    },
    // Indica si la pestaña Registros está activa (carga perezosa).
    active: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      records: [],
      totalRecords: 0,
      page: 0,
      limit: 20,
      limitOptions: [10, 20, 50, 100],
      sortField: 'fechaHoraIngreso',
      sortDirection: 'desc',
      timezone: '',
      loading: false,
      loadedVersion: -1, // versión de filtros ya cargada
      detail: null,
      columns: [
        { key: 'fecha', label: 'Fecha / Hora', sortField: 'fechaHoraIngreso' },
        { key: 'persona', label: 'Persona', sortField: 'nombrePersona' },
        { key: 'rut', label: 'RUT', sortField: 'rutSocio', thClass: 'd-none d-md-table-cell' },
        { key: 'perfil', label: 'Perfil', sortField: 'perfilPersona', thClass: 'd-none d-lg-table-cell' },
        { key: 'estado', label: 'Estado', sortField: 'estadoAcceso' },
        { key: 'metodo', label: 'Método', sortField: 'metodoAcceso', thClass: 'd-none d-sm-table-cell' },
        { key: 'punto', label: 'Punto', sortField: null, thClass: 'd-none d-xl-table-cell' },
        { key: 'sucursal', label: 'Sucursal', sortField: 'nombreSucursalAcceso', thClass: 'd-none d-lg-table-cell' },
        { key: 'plan', label: 'Plan', sortField: 'planAcceso', thClass: 'd-none d-xl-table-cell' }
      ]
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRecords / this.limit));
    },
    // Ventana de hasta 5 páginas centrada en la actual.
    pageWindow() {
      const total = this.totalPages;
      const maxButtons = 5;
      let start = Math.max(0, this.page - 2);
      let end = Math.min(total, start + maxButtons);
      start = Math.max(0, end - maxButtons);
      const arr = [];
      for (let i = start; i < end; i++) arr.push(i);
      return arr;
    },
    detailFields() {
      const d = this.detail || {};
      return [
        { label: 'Persona', value: d.nombrePersona },
        { label: 'RUT', value: d.rutSocio },
        { label: 'Perfil', value: d.perfilPersona },
        { label: 'Estado', value: d.estadoAcceso, badge: true },
        { label: 'Fecha', value: d.fechaIngreso },
        { label: 'Hora', value: d.horaIngreso },
        { label: 'Método de acceso', value: d.metodoAcceso },
        { label: 'Detalle método', value: d.detalleMetodoAcceso || '—' },
        { label: 'Punto de acceso', value: d.idAcceso },
        { label: 'Sucursal', value: d.nombreSucursalAcceso },
        { label: 'Empresa', value: d.nombreEmpresa },
        { label: 'Plan / servicio', value: d.planAcceso },
        { label: 'Valor del plan', value: this.fmtMoney(d.valorPlan) },
        { label: 'Sesiones', value: this.fmtSesiones(d) },
        { label: 'Detalle opcional', value: d.detalleOpcional || '—' }
      ];
    }
  },
  watch: {
    active(val) {
      if (val) this.refreshIfStale();
    },
    paramsVersion() {
      // Si cambian los filtros y la pestaña está activa, recargar de inmediato;
      // si no, marcar como obsoleto para recargar al activarse.
      if (this.active) {
        this.page = 0;
        this.fetchPage();
      }
    }
  },
  mounted() {
    if (this.active) this.refreshIfStale();
  },
  methods: {
    fmt(n) {
      const num = Number(n);
      return isNaN(num) ? (n || 0) : num.toLocaleString('es-CL');
    },
    fmtMoney(v) {
      if (v === null || v === undefined || v === '') return '—';
      const num = Number(v);
      if (isNaN(num)) return v;
      return '$' + num.toLocaleString('es-CL');
    },
    fmtSesiones(d) {
      const u = d.sesionUtilizada;
      const t = d.sesionesTotales;
      if (!t) return '—';
      return `${u || 0} / ${t}`;
    },
    estadoClass(estado) {
      return estado === 'INGRESO' ? 'badge-ingreso' : 'badge-salida';
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'fas fa-sort';
      return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },

    refreshIfStale() {
      if (this.loadedVersion !== this.paramsVersion) {
        this.page = 0;
        this.fetchPage();
      }
    },

    toggleSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'desc';
      }
      this.page = 0;
      this.fetchPage();
    },

    onLimitChange() {
      this.page = 0;
      this.fetchPage();
    },

    goTo(p) {
      if (p < 0 || p >= this.totalPages || p === this.page) return;
      this.page = p;
      this.fetchPage();
    },

    openDetail(record) {
      this.detail = record;
    },

    async fetchPage() {
      this.loading = true;
      try {
        const Parameters = {
          ...this.parameters,
          page: this.page,
          limit: this.limit,
          sortField: this.sortField,
          sortDirection: this.sortDirection
        };
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: OP_REGISTROS },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters,
          Body: {}
        });
        const result = (response.data && response.data.result) || {};
        this.records = result.accesos || [];
        this.totalRecords = result.totalRecords || 0;
        if (typeof result.page === 'number') this.page = result.page;
        if (typeof result.limit === 'number') this.limit = result.limit;
        this.timezone = result.timezone || '';
        this.loadedVersion = this.paramsVersion;
      } catch (error) {
        this.records = [];
        this.totalRecords = 0;
        this.$logger.error('Error obteniendo registros de acceso:', error);
        this.handleRequestError(error);
      } finally {
        this.loading = false;
      }
    },

    handleRequestError(error) {
      const status = error && error.response && error.response.status;
      if (status === 401) return; // gestionado por el interceptor de axios
      let message = 'No se pudieron obtener los registros de acceso.';
      const data = error && error.response && error.response.data;
      if (data && data.error) {
        try {
          const parsed = typeof data.error === 'string' ? JSON.parse(data.error) : data.error;
          if (parsed && parsed.message) message = parsed.message;
        } catch (e) {
          if (typeof data.error === 'string') message = data.error;
        }
      }
      this.$swal.fire({
        icon: status === 400 ? 'warning' : 'error',
        title: status === 400 ? 'Filtros inválidos' : 'Error',
        text: message,
        confirmButtonColor: '#6bd098'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.chart-card {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.registros-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f0f0;
}

.registros-total {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #66615b;

  i { color: #6bd098; }
}

.registros-limit {
  display: flex;
  align-items: center;

  label {
    font-size: 13px;
    color: #9a9a9a;
  }

  .form-select {
    width: auto;
    display: inline-block;
  }
}

.registros-table {
  thead th {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #9a9a9a;
    border-bottom: 2px solid #ececec;
    white-space: nowrap;

    &.sortable {
      cursor: pointer;
      user-select: none;

      &:hover { color: #66615b; }
    }
  }

  .sort-icon {
    margin-left: 4px;
    font-size: 11px;
    opacity: 0.6;
  }

  tbody td {
    vertical-align: middle;
  }
}

.badge-ingreso {
  background: rgba(107, 208, 152, 0.18);
  color: #2f9e63;
}

.badge-salida {
  background: rgba(239, 129, 87, 0.18);
  color: #d35f3a;
}

.registros-pagination {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;

  .registros-pagination-info {
    margin-left: 10px;
    font-size: 13px;
    color: #9a9a9a;
  }
}

/* ===== Modal de detalle (overlay propio, sin dependencia de Bootstrap JS) ===== */
.accesos-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
  padding: 16px;
}

.accesos-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}

.accesos-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  color: #66615b;
}

.btn-close-custom {
  border: none;
  background: transparent;
  color: #9a9a9a;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;

  &:hover { color: #66615b; }
}

.accesos-modal-body {
  padding: 8px 20px;
  overflow-y: auto;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid #f4f4f4;

  &:last-child { border-bottom: none; }
}

.detail-label {
  font-size: 13px;
  color: #9a9a9a;
  flex-shrink: 0;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #2c2c2c;
  text-align: right;
  word-break: break-word;
}

.accesos-modal-footer {
  padding: 14px 20px;
  border-top: 1px solid #eee;
  text-align: right;
}
</style>
